import { useState } from "react";
import { Modal, Platform, Pressable, View } from "react-native";
import { Calendar, type LucideIcon } from "lucide-react-native";
import DateTimePicker, {
  type DateTimePickerEvent,
} from "@react-native-community/datetimepicker";
import {
  isoToDisplay,
  placeholderFor,
  useDateFormat,
} from "@src/packages/utils/useDateFormat";
import { colors, semanticColors } from "../theme/tokens";
import { MoonFormField } from "./MoonFormField";
import { MoonFieldInput } from "./MoonFieldInput";
import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
import { useTranslations } from "@src/packages/i18n";
import { TextActionButton } from "./TextActionButton";
import { DateTextInput } from "./DateTextInput";
import { MoonText } from "./MoonText";
import { useInputFontSize } from "../utils/inputFontSize";

/**
 * Typed date field with platform-aware editing:
 *
 *   - **iOS / Android native**: Pressable trigger shows the formatted date
 *     (or the format placeholder when empty); tap opens the OS-native
 *     date picker via `@react-native-community/datetimepicker`. iOS uses
 *     a modal spinner, Android the native modal dialog.
 *   - **Web (all viewport widths)**: typeable text input with
 *     auto-inserted separators per `dateFormat`.
 *
 * The calendar icon sits on the label, like every other field's icon: a
 * plain label gets the calendar, and a label with its own icon keeps it. The
 * input only shows the calendar when the field has no label at all. The
 * glass (onboarding) appearance shows neither.
 *
 * `value` is the canonical "YYYY-MM-DD" string on every surface.
 */
type Props = {
  label?: string;
  /** Decorative Lucide icon rendered immediately before the field label. */
  labelIcon?: LucideIcon;
  /** Supporting copy rendered directly below the label and above the input. */
  labelHint?: string;
  /** "YYYY-MM-DD" or "" for empty. */
  value: string;
  onChange: (next: string) => void;
  error?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  loading?: boolean;
  testID?: string;
  appearance?: "default" | "glass";
  accessibilityLabel?: string;
  autoFocus?: boolean;
  onValidityChange?: (valid: boolean) => void;
};

function isoToDate(iso: string): Date {
  if (!iso) return new Date();
  const d = new Date(iso + "T00:00:00");
  return isNaN(d.getTime()) ? new Date() : d;
}

function dateToIso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

export function DateField({
  label,
  labelIcon,
  labelHint,
  value,
  onChange,
  error,
  hint,
  required,
  disabled,
  loading,
  testID,
  appearance = "default",
  accessibilityLabel,
  autoFocus,
  onValidityChange,
}: Props) {
  const isWeb = Platform.OS === "web";
  const t = useTranslations();
  const inputLabel =
    accessibilityLabel ?? (label || t("date_picker.enter_date"));
  const isGlass = appearance === "glass";
  const resolvedLabelIcon =
    labelIcon ?? (label && !isGlass ? Calendar : undefined);
  const showLeadingIcon = !label && !isGlass;
  const handleChange = (next: string) => {
    onValidityChange?.(Boolean(next));
    onChange(next);
  };
  // Half a typed date is not a date. The web field keeps her text and says so
  // here, rather than saving the old or empty value without a word. The native
  // picker only ever gives a whole date.
  const [incomplete, setIncomplete] = useState(false);
  const shownError =
    error || (incomplete ? t("date_picker.enter_the_full_date") : undefined);

  // All web surfaces (desktop and narrow browser windows) use the
  // typeable input. The previous `width < 768` branch routed to a
  // `<input type="date">` overlay that triggered the browser's native
  // date popover — confusing on a constrained desktop window where the
  // user expected to just type the date. Real iOS / Android (where the
  // OS picker actually shines) goes through `NativeTriggerDate`
  // regardless of viewport.
  return (
    <MoonFormField
      label={label}
      labelIcon={resolvedLabelIcon}
      labelHint={labelHint}
      error={shownError}
      hint={hint}
      required={required}
      disabled={disabled}
      loading={loading}
      loadingHeight={appearance === "glass" ? 58 : 44}
      tone={appearance === "glass" ? "dark" : "default"}
      testID={testID}
    >
      {isWeb ? (
        <DateTextInput
          value={value}
          onChange={onChange}
          hasError={!!shownError}
          disabled={disabled}
          autoFocus={autoFocus}
          onValidityChange={onValidityChange}
          onIncompleteChange={setIncomplete}
          showLeadingIcon={showLeadingIcon}
          appearance={appearance}
          accessibilityLabel={inputLabel}
        />
      ) : (
        <NativeTriggerDate
          value={value}
          onChange={handleChange}
          hasError={!!error}
          disabled={disabled}
          showLeadingIcon={showLeadingIcon}
          appearance={appearance}
          accessibilityLabel={inputLabel}
        />
      )}
    </MoonFormField>
  );
}

/**
 * Shared native trigger row. Shows the formatted date or placeholder
 * and a calendar when the label has no icon. Stays presentational.
 */
function DateTriggerRow({
  displayText,
  isEmpty,
  fontSize,
  showLeadingIcon,
  appearance,
}: {
  displayText: string;
  isEmpty: boolean;
  fontSize: number;
  showLeadingIcon: boolean;
  appearance: "default" | "glass";
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        paddingVertical: appearance === "glass" ? 14 : 10,
        gap: showLeadingIcon ? 10 : 0,
      }}
    >
      {showLeadingIcon ? (
        <View style={{ flexShrink: 0 }}>
          <Calendar size={13} color={colors.textLight} strokeWidth={1.5} />
        </View>
      ) : null}
      <MoonText
        style={{
          flex: 1,
          minWidth: 0,
          fontSize,
          letterSpacing: appearance === "glass" ? 1.5 : 0,
          color:
            appearance === "glass"
              ? isEmpty
                ? semanticColors.onDark.text.tertiary
                : semanticColors.onDark.text.primary
              : isEmpty
                ? colors.textFaint
                : colors.text,
          textAlign: appearance === "glass" ? "center" : "left",
          lineHeight: 20,
        }}
      >
        {displayText}
      </MoonText>
    </View>
  );
}

function NativeTriggerDate({
  value,
  onChange,
  placeholder,
  hasError,
  disabled,
  showLeadingIcon,
  appearance,
  accessibilityLabel,
}: {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  hasError: boolean;
  disabled?: boolean;
  showLeadingIcon: boolean;
  appearance: "default" | "glass";
  accessibilityLabel: string;
}) {
  const { dateFormat } = useDateFormat();
  const fontSize = useInputFontSize(
    appearance === "glass" ? "large" : "adaptive",
  );
  const capturePress = usePressCapture("DateField");
  const t = useTranslations();
  const [open, setOpen] = useState(false);
  const placeholderText = placeholder ?? placeholderFor(dateFormat);
  const displayText = value ? isoToDisplay(value, dateFormat) : placeholderText;
  const isEmpty = !value;

  return (
    <MoonFieldInput
      hasError={hasError}
      appearance={appearance}
      paddingY={appearance === "glass" ? 0 : 2}
      style={{ justifyContent: "center" }}
    >
      <Pressable
        onPress={() => {
          if (disabled) return;
          capturePress(pressLabel(displayText), "open_date_picker");
          setOpen(true);
        }}
        disabled={disabled}
        accessibilityRole="button"
        accessibilityLabel={`${accessibilityLabel}: ${displayText}`}
      >
        <DateTriggerRow
          displayText={displayText}
          isEmpty={isEmpty}
          fontSize={fontSize}
          showLeadingIcon={showLeadingIcon}
          appearance={appearance}
        />
      </Pressable>
      {open && Platform.OS === "ios" ? (
        <Modal
          visible
          transparent
          animationType="fade"
          onRequestClose={() => setOpen(false)}
        >
          <Pressable
            onPress={() => setOpen(false)}
            style={{
              flex: 1,
              backgroundColor: "rgba(0,0,0,0.4)",
              justifyContent: "flex-end",
            }}
          >
            <Pressable
              onPress={(e) => e.stopPropagation()}
              style={{
                width: "100%",
                alignItems: "center",
                backgroundColor: "#FFFFFF",
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
                paddingTop: 8,
                paddingBottom: 24,
              }}
            >
              <DateTimePicker
                value={isoToDate(value)}
                mode="date"
                display="spinner"
                onChange={(_: DateTimePickerEvent, selected?: Date) => {
                  if (selected) onChange(dateToIso(selected));
                }}
              />
              <TextActionButton
                label={t("date_picker.confirm_date")}
                action="confirm_date"
                centered
                onPress={() => setOpen(false)}
                style={{ marginTop: 4 }}
              />
            </Pressable>
          </Pressable>
        </Modal>
      ) : null}
      {open && Platform.OS === "android" ? (
        <DateTimePicker
          value={isoToDate(value)}
          mode="date"
          display="default"
          onChange={(event: DateTimePickerEvent, selected?: Date) => {
            setOpen(false);
            if (event.type === "set" && selected) {
              onChange(dateToIso(selected));
            }
          }}
        />
      ) : null}
    </MoonFieldInput>
  );
}
